import type {GuideItem} from '../../types';

import {CloseOutlined, LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button, IconButton} from '@ds/components';
import {cn} from '@ds/helpers';
import {useEffect, useState} from 'react';
import images from 'utils/requireImages';

type AssortmentGuideProps = {
  items: GuideItem[];
  onClose: () => void;
};

const AssortmentGuide: React.FC<AssortmentGuideProps> = ({items, onClose}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = items[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === items.length - 1;

  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }
    setActiveIndex((currentIndex) => currentIndex + 1);
  };

  return (
    <div className="relative flex h-full flex-col bg-gray-08 text-white">
      <div className="px-5.5 py-4.5">
        <IconButton
          aria-label="بستن راهنما"
          className="border border-white text-white"
          color="secondary"
          variant="outline"
          size="sm"
          onClick={onClose}
        >
          <CloseOutlined aria-hidden />
        </IconButton>
      </div>

      <div className="h-70 px-5.5 pt-2.5">
        <div className="h-62 overflow-hidden rounded-xl bg-orange-wash">
          <img
            className="size-full object-contain"
            src={activeItem?.image ? images[activeItem.image] : undefined}
            alt=""
            aria-hidden
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-7.25 py-3 text-body-xs text-white">
        {activeItem?.textImage ? (
          <img className="w-full" src={images[activeItem.textImage]} alt="" aria-hidden />
        ) : (
          <>
            {activeItem?.text && <p className="text-justify">{activeItem.text}</p>}
            {activeItem?.details && (
              <div className="mt-4 flex flex-col gap-2">
                {activeItem.details.map((detail) => (
                  <p key={detail.title}>
                    <span className="text-body-xs-emphasis">{detail.title}: </span>
                    {detail.description}
                  </p>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-4 px-4 pb-6">
        <div className="flex flex-col items-center gap-2">
          <span className="text-body-xs-emphasis">
            صفحه {activeIndex + 1}/{items.length}
          </span>
          <div className="flex h-1.5 w-full gap-2.5">
            {items.map((item, index) => (
              <span
                key={item.id}
                className={cn(
                  'h-full flex-1 rounded-full bg-gray-05 transition-[flex-grow,background-color]',
                  index === activeIndex && 'grow-2 bg-white'
                )}
                aria-hidden
              />
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            className="flex-1"
            color="secondary"
            size="lg"
            startIcon={<RightOutlined aria-hidden />}
            disabled={isFirst}
            onClick={() => setActiveIndex((currentIndex) => currentIndex - 1)}
          >
            برو قبلی
          </Button>
          <Button
            className={cn('flex-1', isLast && 'bg-orange! text-white!')}
            color="secondary"
            size="lg"
            endIcon={!isLast ? <LeftOutlined aria-hidden /> : undefined}
            onClick={handleNext}
          >
            {isLast ? 'متوجه شدم' : 'برو بعدی'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssortmentGuide;
